import { memo, useCallback } from 'react';
import _get from 'lodash.get';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import {
  jsDateToLocalISO8601DateString,
  getDateFromDateString,
  convertDateToTimezone,
  getLocalTimeZoneId,
} from '../../../lib';
import { useStyles } from './inputs.styles';
import { TextFieldProps } from '@mui/material/TextField';
import { FormikProps } from 'formik';

type FormikDateInputProps = Omit<
  DatePickerProps<false>,
  'value' | 'onChange' | 'renderInput'
> & {
  name: string;
  label?: string;
  type?: string;
  formikProps: FormikProps<any>;
  handleChange?: (event: {
    target: { name: string; value: string | null };
  }) => void;
  handleBlur?: (event: React.FocusEvent<any>) => void;
  className?: string;
  format?: string;
  required?: boolean;
  validationKey?: string;
  textFieldProps?: TextFieldProps;
};

export const FormikDateInput = memo(function FormikDateInput({
  name,
  label,
  type = 'text',
  formikProps,
  handleChange,
  handleBlur,
  className,
  format,
  required,
  validationKey,
  textFieldProps: _textFieldProps,
  ...otherProps
}: FormikDateInputProps) {
  const { classes, cx } = useStyles();
  const {
    handleChange: formikHandleChange,
    handleBlur: formikHandleBlur,
    values,
    touched,
    errors,
  } = formikProps;

  const textFieldProps = _textFieldProps || {};

  const onBlur = useCallback(
    (e: React.FocusEvent<any>) => {
      // Create a new event with the field name explicitly set
      // https://formik.org/docs/api/formik#handleblur-e-any--void
      const eventWithName = {
        ...e,
        target: {
          ...e.target,
          name,
        },
      };
      formikHandleBlur(eventWithName);
      handleBlur?.(eventWithName);
    },
    [formikHandleBlur, handleBlur, name],
  );

  const onChange = useCallback(
    (date: Date | null) => {
      const value = date ? jsDateToLocalISO8601DateString(date) : null;
      const event = { target: { name, value } };
      formikHandleChange?.(event);
      handleChange?.(event);
    },
    [formikHandleChange, handleChange, name],
  );

  // On blur touched is set on the name but on submit it's set on the nested object so we need to check both
  const isTouched = !!(
    _get(touched, name as string) || _get(touched, validationKey as string)
  );
  const errorText = _get(errors, (validationKey ?? name) as string) as string;

  return (
    <DatePicker
      value={toLocalDate(values[name]) ?? null}
      format={format ?? 'MM/dd/yyyy'}
      onChange={onChange}
      name={name}
      slotProps={{
        textField: {
          name,
          variant: 'standard',
          onBlur,
          error: isTouched && !!errorText,
          helperText: isTouched ? errorText : undefined,
          className: cx(classes.inputRoot, className),
          InputLabelProps: { className: classes.label },
          type,
          label,
          required,
          ...textFieldProps,
        },
      }}
      {...otherProps}
    />
  );
});

function toLocalDate(s) {
  if (!s) return null;
  if (s instanceof Date)
    return convertDateToTimezone({ date: s, timezone: getLocalTimeZoneId() });
  return getDateFromDateString({ dateStr: s });
}
